/**
 * Chart Data Fetchers
 * Specialized functions that handle telemetryService responses and extract chart data
 * Used with useChartPropsPattern to ensure proper data structure handling
 */

/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import {
  telemetryService,
  StrokeData,
  GateForceStrokeSamplerResult,
  GateForceProfileResult,
} from '../services/telemetry/telemetryService';
import { DEBUG } from '../config/debug';

/**
 * Power time series data structure (what PowerAnalysisChart expects)
 */
export interface PowerTimeSeriesData {
  time: number;
  [rowerKey: string]: number;
}

export interface PowerTimeSeriesMeta {
  data: PowerTimeSeriesData[] | null;
  strokeBoundaries?: Array<{ start: number; end: number | null }>;
}

/**
 * Performance metrics data structure (what PerformanceMetrics expects)
 */
export interface PerformanceMetricsData {
  averagePowerByRower: { [rower: number]: number };
  overallAverage: number;
  crewAverage: number;
  sessionDuration: number;
  dataPointCount: number;
  rowerPowers: { [rower: string]: number };
  strokeCount: number;
  individualStrokes: { [rower: string]: Array<any> };
  powerRange: { min: number; max: number };
  peakPower: number;
  consistency: number;
  seatIdMapping?: { [position: number]: string } | null;
  enhancedRowerData: Array<{
    id: number;
    power: number;
    min: number;
    max: number;
    p25: number;
    p75: number;
    peak: number;
    consistency: number;
    powerDistribution: Array<{
      start: number;
      end: number;
      count: number;
      frequency: number;
    }>;
  }>;
}

/**
 * Telemetry data structure for interval-based power data
 */
export interface TelemetryData {
  interval: string;
  [rowerKey: string]: string | number;
}

/**
 * Data fetcher for PowerAnalysisChart
 * Only works with Periodic data (GateAngle, GateForceX, GateAngleVelocity)
 * Aperiodic data (SwivelPower) should show "no data available" message
 */
export async function fetchPowerTimeSeriesData(
  sessionId: string
): Promise<PowerTimeSeriesData[] | null> {
  // Power Analysis Chart ONLY uses physics-based calculations from Periodic data
  // It should NEVER use SwivelPower data (Aperiodic data)
  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    // eslint-disable-next-line no-console
    console.log(
      '[CHART_DATA] Power Analysis Chart: Getting physics-based power from Periodic data only'
    );
  }

  // Use sampler for better performance (avoids 50+ sequential queries)
  const samplerResponse = await telemetryService.getSampledPowerTimeSeries(sessionId, 1200);

  if (samplerResponse.success && samplerResponse.data?.length > 0) {
    if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
      // eslint-disable-next-line no-console
      console.log('[CHART_DATA] Using sampler for power data - much faster!');
    }
    return samplerResponse.data;
  }

  // If sampler fails, return null instead of expensive fallback
  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    // eslint-disable-next-line no-console
    console.warn('[CHART_DATA] Sampler failed, returning null to avoid expensive query');
  }
  return null;

  // NOTE: Expensive fallback disabled - was causing 50+ sequential queries
  // Old code commented out to prevent 50+ queries that freeze the browser:
  /*
  const physicsResponse = await telemetryService.getPowerOutputTimeSeries(sessionId);

  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    console.log('[CHART_DATA][DEBUG] getPowerOutputTimeSeries result:', physicsResponse);
  }

  const hasPeriodic =
    (Array.isArray(physicsResponse.data) && physicsResponse.data.length > 0) ||
    (typeof physicsResponse.totalRows === 'number' && physicsResponse.totalRows > 0) ||
    (typeof physicsResponse.rawCount === 'number' && physicsResponse.rawCount > 0);

  if (physicsResponse.success && hasPeriodic) {
    if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
      console.log('[CHART_DATA] Power Analysis Chart: Successfully got physics-based power data');
    }
    if (physicsResponse.data?.length > 0) {
      return physicsResponse.data;
    } else {
      return physicsResponse.data;
    }
  }

  const reason = physicsResponse.reason || 'unknown';
  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    console.log(`[CHART_DATA] No Periodic data available - reason: ${reason}`);
  }
  return null;
  */
}

/**
 * Legacy data fetcher for PowerAnalysisChart using physics calculations only
 * Use this if you specifically want to force physics-based calculations
 */
export async function fetchPhysicsPowerTimeSeriesData(
  sessionId: string
): Promise<PowerTimeSeriesData[] | null> {
  const response = await telemetryService.getPowerOutputTimeSeries(sessionId);

  if (!response.success || !response.data) {
    console.log('[CHART_DATA] Failed to load power time series data');
    return null;
  }

  return response.data;
}

/**
 * Data fetcher for PowerAnalysisChart using physics-based calculations only
 * This is specifically for the PowerAnalysisChart component to use physics-based power
 * while keeping other dashboard components using pre-calculated swivel power data
 */
export async function fetchPhysicsBasedPowerTimeSeriesData(
  sessionId: string
): Promise<PowerTimeSeriesData[] | null> {
  const response = await telemetryService.getPowerOutputTimeSeries(sessionId);

  if (!response.success || !response.data) {
    if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
      // eslint-disable-next-line no-console
      console.log('[CHART_DATA] Failed to load physics-based power time series data');
    }
    return null;
  }

  return response.data;
}

/**
 * Physics-based time series with metadata (stroke boundaries)
 * Used for stroke-aware resampling in high-fidelity rendering
 * Now uses RPC sampling for better performance with large datasets
 */
export async function fetchPhysicsBasedPowerTimeSeriesWithMeta(
  sessionId: string,
  rowerContext?: import('../types/rowerContext').RowerContext | null
): Promise<PowerTimeSeriesMeta> {
  // Determine rowerPositions based on rowerContext
  // If rowerContext.mode === 'filter', only fetch that rower's data (compliance)
  const rowerPositions =
    rowerContext && rowerContext.mode === 'filter' ? [rowerContext.position] : undefined;

  // Try RPC sampling first (much faster for large datasets)
  const sampledResponse = await telemetryService.getSampledPowerTimeSeries(
    sessionId,
    1200,
    rowerPositions
  );

  if (sampledResponse.success && sampledResponse.data?.length > 0) {
    if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
      console.log('[CHART_DATA] Using RPC-sampled power data for better performance');
    }
    return { data: sampledResponse.data, strokeBoundaries: [] };
  }

  // Fallback to full physics calculation if RPC fails
  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    console.log('[CHART_DATA] RPC sampling failed, falling back to full physics calculation');
  }

  const response = await telemetryService.getPowerOutputTimeSeries(sessionId);
  if (!response.success) {
    return { data: null, strokeBoundaries: [] };
  }

  // Filter data if rowerContext.mode === 'filter' (compliance: rowers should only see their own data)
  let filteredData = response.data;
  if (rowerContext && rowerContext.mode === 'filter' && filteredData && filteredData.length > 0) {
    const focusedRowerKey = `Rower ${rowerContext.position}`;
    filteredData = filteredData.map((point) => {
      const filteredPoint: any = { time: point.time };
      // Only include the focused rower's data
      if (focusedRowerKey in point) {
        filteredPoint[focusedRowerKey] = point[focusedRowerKey];
      }
      return filteredPoint;
    });
  }

  return { data: filteredData, strokeBoundaries: response.strokeBoundaries || [] };
}

/**
 * Data fetcher for PowerAnalysisChart using SwivelPower data (Aperiodic)
 * Fetches pre-calculated SwivelPower values from the 0x800A section
 */
export async function fetchSwivelPowerTimeSeriesData(
  sessionId: string
): Promise<PowerTimeSeriesData[] | null> {
  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    // eslint-disable-next-line no-console
    console.log('[CHART_DATA] Power Analysis Chart: Getting SwivelPower data from Aperiodic data');
  }

  const response = await telemetryService.getSwivelPowerTimeSeries(sessionId);

  if (!response.success || !response.data) {
    if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
      // eslint-disable-next-line no-console
      console.log('[CHART_DATA] Power Analysis Chart: No SwivelPower data available');
    }
    return null;
  }

  if (DEBUG.ALL || DEBUG.POWER || DEBUG.TELEMETRY) {
    // eslint-disable-next-line no-console
    console.log(
      '[CHART_DATA] Power Analysis Chart: Successfully got SwivelPower data from Aperiodic data'
    );
  }
  return response.data;
}

/**
 * Data fetcher for PerformanceMetrics
 * Only works with Aperiodic data (SwivelPower values from 0x800A)
 * Periodic data should show "no data available" message
 */
export async function fetchPerformanceMetricsData(
  sessionId: string
): Promise<PerformanceMetricsData | null> {
  try {
    // Try to get pre-calculated SwivelPower data (Aperiodic 0x800A)
    const swivelResponse = await telemetryService.getSwivelPowerAveragePower(sessionId);

    if (swivelResponse.success && swivelResponse.data) {
      console.log(
        '🔍 CHART_DATA_005: Using pre-calculated SwivelPower data for PerformanceMetrics'
      );
      return swivelResponse.data;
    }

    // No SwivelPower data available - Performance Metrics only works with Aperiodic data
    console.log(
      '[CHART_DATA] Performance Metrics requires Aperiodic data (SwivelPower values from 0x800A section)'
    );
    return null;
  } catch (error) {
    // Report data fetching errors to Sentry
    Sentry.captureException(error, {
      tags: { component: 'chart-data-fetcher', operation: 'fetch_performance_metrics' },
      extra: { sessionId },
    });
    throw error;
  }
}

/**
 * Data fetcher for telemetry interval data
 * Extracts telemetry interval data from getPowerOutputData
 */
export async function fetchTelemetryData(sessionId: string): Promise<TelemetryData[] | null> {
  const data = await telemetryService.getPowerOutputData(sessionId);

  if (!data || data.length === 0) {
    console.log('[CHART_DATA] Failed to load telemetry data');
    return null;
  }

  return data;
}

/**
 * Data fetcher for Gate Force Signature chart
 * Extracts stroke data from getDecimatedStrokeData response
 * Only works with Periodic data (GateAngle, GateForceX from telemetry_data table)
 * Note: Requires rowerPosition parameter - charts should handle multi-rower data fetching
 */
export async function fetchStrokeData(
  sessionId: string,
  rowerPosition: number = 1,
  targetPoints: number = 1000
): Promise<StrokeData[] | null> {
  const data = await telemetryService.getDecimatedStrokeData(
    sessionId,
    rowerPosition,
    targetPoints
  );

  if (!data || data.length === 0) {
    // Return null instead of throwing error to prevent React Query infinite loops
    // The chart component should handle null data by showing "no data available" message
    console.log(
      `[CHART_DATA] No stroke data available for rower ${rowerPosition} in session ${sessionId}`
    );
    return null;
  }

  return data;
}

/**
 * Raw stroke data fetcher (no decimation)
 * Returns full-resolution telemetry for a given rower
 */
export async function fetchRawStrokeData(
  sessionId: string,
  rowerPosition: number = 1
): Promise<StrokeData[] | null> {
  const data = await telemetryService.getStrokeData(sessionId, rowerPosition);

  if (!data || data.length === 0) {
    console.log(
      `[CHART_DATA] No raw stroke data available for rower ${rowerPosition} in session ${sessionId}`
    );
    return null;
  }

  return data;
}

const BUILD_TIME_GATE_FORCE_SAMPLER_FLAG = process.env.REACT_APP_GATE_FORCE_USE_SAMPLER as
  | string
  | undefined;

const resolveGateForceSamplerFlag = (): string | undefined => {
  if (BUILD_TIME_GATE_FORCE_SAMPLER_FLAG && BUILD_TIME_GATE_FORCE_SAMPLER_FLAG.trim().length > 0) {
    return BUILD_TIME_GATE_FORCE_SAMPLER_FLAG;
  }
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const runtimeEnv = (window as any).__env;
    if (runtimeEnv && typeof runtimeEnv.REACT_APP_GATE_FORCE_USE_SAMPLER === 'string') {
      return runtimeEnv.REACT_APP_GATE_FORCE_USE_SAMPLER;
    }
  }
  return undefined;
};

export const isGateForceSamplerEnabled = (): boolean => {
  const flag = (resolveGateForceSamplerFlag() || '').trim().toLowerCase();
  return flag === 'true' || flag === '1';
};

export async function fetchGateForceStrokeSamplerData(
  sessionId: string,
  options?: { rowerPositions?: number[]; targetPoints?: number; minPointsPerStroke?: number }
): Promise<GateForceStrokeSamplerResult | null> {
  if (!isGateForceSamplerEnabled()) {
    return null;
  }

  try {
    const result = await telemetryService.getGateForceStrokeSamples(sessionId, options);
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[CHART_DATA] Failed to fetch stroke sampler data:', error);
    return null;
  }
}

export async function fetchGateForceMedianProfileSamplerData(
  sessionId: string,
  options?: { rowerPositions?: number[]; targetPoints?: number; minPointsPerStroke?: number }
): Promise<GateForceProfileResult | null> {
  if (!isGateForceSamplerEnabled()) {
    return null;
  }

  try {
    const result = await telemetryService.getGateForceMedianProfile(sessionId, options);
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[CHART_DATA] Failed to fetch median profile sampler data:', error);
    return null;
  }
}

/**
 * Validate data fetcher responses for debugging
 */
export function validateChartData<T>(data: T, chartName: string): T {
  if (!data) {
    console.warn(`${chartName}: Received null/undefined data`);
    throw new Error(`No data available for ${chartName}`);
  }

  if (Array.isArray(data) && data.length === 0) {
    console.warn(`${chartName}: Received empty data array`);
    throw new Error(`No data points available for ${chartName}`);
  }

  return data;
}
